import React, { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Save, Check, X, Loader2, Key, Database, Cloud } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SettingsTab({ settings, loadSettings, driveConnected, disconnectDrive }) {
  const [claudeApiKey, setClaudeApiKey] = useState("");
  const [airtableApiKey, setAirtableApiKey] = useState("");
  const [airtableBaseId, setAirtableBaseId] = useState("");
  const [airtableTableName, setAirtableTableName] = useState("Projects");
  const [googleClientId, setGoogleClientId] = useState("");
  const [googleClientSecret, setGoogleClientSecret] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/settings/save`, {
        claude_api_key: claudeApiKey,
        airtable_api_key: airtableApiKey,
        airtable_base_id: airtableBaseId,
        airtable_table_name: airtableTableName,
        google_client_id: googleClientId,
        google_client_secret: googleClientSecret
      });

      toast.success("Settings saved successfully!");
      loadSettings();
      
      // Clear sensitive fields
      setClaudeApiKey("");
      setAirtableApiKey("");
      setGoogleClientId("");
      setGoogleClientSecret("");
    } catch (error) {
      console.error("Save settings error:", error);
      toast.error("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-bold text-white">Settings</h2>
        <p className="text-white/60">Configure your API keys and integrations</p>
      </div>

      {/* Claude AI Settings */}
      <Card className="card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Key className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <CardTitle className="text-white">Claude AI</CardTitle>
              <CardDescription className="text-white/60">
                Required for content generation
              </CardDescription>
            </div>
            {settings.hasClaude && (
              <div className="ml-auto">
                <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                  <Check className="w-4 h-4" />
                  Connected
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="claude-key" className="text-white/80">
              Claude API Key
            </Label>
            <Input
              id="claude-key"
              type="password"
              value={claudeApiKey}
              onChange={(e) => setClaudeApiKey(e.target.value)}
              placeholder="sk-ant-api..."
              className="bg-white/5 border-white/10 text-white mt-2"
            />
            <p className="text-xs text-white/40 mt-2">
              Get your API key from{" "}
              <a
                href="https://console.anthropic.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00d4aa] hover:underline"
              >
                console.anthropic.com
              </a>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Google Drive Settings */}
      <Card className="card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Cloud className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-white">Google Drive</CardTitle>
              <CardDescription className="text-white/60">
                Load documents from Google Drive
              </CardDescription>
            </div>
            {driveConnected ? (
              <div className="ml-auto">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                    <Check className="w-4 h-4" />
                    Connected
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={disconnectDrive}
                    className="text-red-400 hover:bg-red-500/10"
                  >
                    Disconnect
                  </Button>
                </div>
              </div>
            ) : (
              <div className="ml-auto">
                <div className="flex items-center gap-2 px-3 py-1 bg-white/10 text-white/60 rounded-full text-sm">
                  <X className="w-4 h-4" />
                  Not Connected
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="google-client-id" className="text-white/80">
                Google Client ID
              </Label>
              <Input
                id="google-client-id"
                type="text"
                value={googleClientId}
                onChange={(e) => setGoogleClientId(e.target.value)}
                placeholder="xxxxx.apps.googleusercontent.com"
                className="bg-white/5 border-white/10 text-white mt-2"
              />
            </div>
            <div>
              <Label htmlFor="google-client-secret" className="text-white/80">
                Google Client Secret
              </Label>
              <Input
                id="google-client-secret"
                type="password"
                value={googleClientSecret}
                onChange={(e) => setGoogleClientSecret(e.target.value)}
                placeholder="GOCSPX-..."
                className="bg-white/5 border-white/10 text-white mt-2"
              />
            </div>
          </div>
          <p className="text-xs text-white/40">
            Create OAuth credentials at{" "}
            <a
              href="https://console.cloud.google.com/apis/credentials"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#00d4aa] hover:underline"
            >
              Google Cloud Console
            </a>
          </p>
        </CardContent>
      </Card>

      {/* Airtable Settings */}
      <Card className="card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#00d4aa]/20 rounded-lg">
              <Database className="w-5 h-5 text-[#00d4aa]" />
            </div>
            <div>
              <CardTitle className="text-white">Airtable</CardTitle>
              <CardDescription className="text-white/60">
                Save and load projects from Airtable
              </CardDescription>
            </div>
            {settings.hasAirtable && (
              <div className="ml-auto">
                <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                  <Check className="w-4 h-4" />
                  Connected
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="airtable-key" className="text-white/80">
              Airtable API Key
            </Label>
            <Input
              id="airtable-key"
              type="password"
              value={airtableApiKey}
              onChange={(e) => setAirtableApiKey(e.target.value)}
              placeholder="pat..."
              className="bg-white/5 border-white/10 text-white mt-2"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="airtable-base" className="text-white/80">
                Base ID
              </Label>
              <Input
                id="airtable-base"
                type="text"
                value={airtableBaseId}
                onChange={(e) => setAirtableBaseId(e.target.value)}
                placeholder="appXXXXXXXXXXXXXXXX"
                className="bg-white/5 border-white/10 text-white mt-2"
              />
            </div>
            <div>
              <Label htmlFor="airtable-table" className="text-white/80">
                Table Name
              </Label>
              <Input
                id="airtable-table"
                type="text"
                value={airtableTableName}
                onChange={(e) => setAirtableTableName(e.target.value)}
                placeholder="Projects"
                className="bg-white/5 border-white/10 text-white mt-2"
              />
            </div>
          </div>
          <p className="text-xs text-white/40">
            Find your credentials at{" "}
            <a
              href="https://airtable.com/create/tokens"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#00d4aa] hover:underline"
            >
              airtable.com/create/tokens
            </a>
          </p>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Button
        onClick={saveSettings}
        disabled={isSaving}
        className="w-full h-12 bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold text-lg"
      >
        {isSaving ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Save className="w-5 h-5 mr-2" />
            Save All Settings
          </>
        )}
      </Button>

      {/* Info Box */}
      <div className="p-6 bg-white/5 rounded-lg text-center border border-white/10">
        <p className="text-white/60 text-sm">
          \ud83d\udd12 All API keys are securely stored and never shared. They&apos;re only used to
          make requests on your behalf.
        </p>
      </div>
    </div>
  );
}

export default SettingsTab;
